/**
 * WordPress provides React during the page load. This is for TypeScript type checking only.
 *
 * Importing React from @wordpress/element causes "Cannot read property 'createElement' of undefined"
 */
import React from 'react';
import { memo, useState, useEffect } from '@wordpress/element';
import { useSelect, useDispatch } from '@wordpress/data';
import {
	BlockControls,
	useBlockProps,
	InnerBlocks,
	InspectorControls,
	BlockContextProvider,
	store as blockEditorStore,
	useInnerBlocksProps,
} from '@wordpress/block-editor';
import { __ } from '@wordpress/i18n';
import {
	PanelBody,
	TextareaControl,
	ToolbarGroup,
} from '@wordpress/components';
import { list, grid } from '@wordpress/icons';
import classnames from 'classnames';
import BlockPreview from '../../components/BlockPreview';
import { useViewDisplay } from '../../hooks';

export interface GPEntriesLoopBlockAttributes {
	noEntriesFoundMessage: string;
	layout?: any;
}

function EntryTemplateBlockPreview({
	blockArgs,
	blockContextId,
	isHidden,
	setActiveBlockContextId,
	fadeOut = false,
}) {
	const handleOnClick = () => {
		setActiveBlockContextId(blockContextId);
	};

	const style = {
		display: isHidden ? 'none' : undefined,
		opacity: fadeOut ? 0.35 : undefined,
	};

	const blockPreviewArgs = {
		...blockArgs,
		includeContent: true,
		tabIndex: 0,
		role: 'button',
		onClick: handleOnClick,
		onKeyPress: handleOnClick,
		style,
	};

	return <BlockPreview {...blockPreviewArgs} />;
}

const MemoizedEntryTemplateBlockPreview = memo(EntryTemplateBlockPreview);

// @todo should this be renamed to something like Entry Loop Template?
// @todo can we add some sort of toggle in the block controls to render the loop using something similar to <BlockPreview>?
const Edit = (args: BlockProps<GPEntriesLoopBlockAttributes>) => {
	const { setAttributes, attributes } = args;
	const { layout } = attributes;
	const { type: layoutType, columnCount = 3 } = layout || {};
	const { invalidateResolution } = useDispatch('gp-entry-blocks');

	const blockProps = useBlockProps({
		// @ts-ignore
		className: classnames(args.__unstableLayoutClassNames, {
			[`columns-${columnCount}`]: layoutType === 'grid' && columnCount,
		}),
	});

	const viewDisplay = useViewDisplay('entries');

	blockProps.style = {
		...(blockProps.style ?? {}),
		display: viewDisplay,
	};

	const innerBlocksProps = useInnerBlocksProps(
		{
			renderAppender: InnerBlocks.ButtonBlockAppender,
		},
		{
			// Prevent nested grids.
			__unstableDisableLayoutClassNames: true,
		}
	);

	const [activeBlockContextId, setActiveBlockContextId] = useState();

	const isBlockOrChildSelected = useSelect(
		(select) => {
			const { getSelectedBlockClientId, getBlockParents } =
				select(blockEditorStore);

			// Current Entries Loop Block
			if (getSelectedBlockClientId() === args.clientId) {
				return true;
			}

			// Inner blocks
			const parents = getBlockParents(getSelectedBlockClientId());

			if (parents.includes(args.clientId)) {
				return true;
			}

			return false;
		},
		[args.clientId]
	);

	const inspectorControls = (
		<InspectorControls>
			{args.context['gp-entry-blocks/formId'] && (
				<>
					<PanelBody
						className="gpeb_panel_entries_table_settings"
						title={__('Messages', 'gp-entry-blocks')}
					>
						<TextareaControl
							value={args.attributes.noEntriesFoundMessage}
							onChange={(noEntriesFoundMessage) =>
								args.setAttributes({ noEntriesFoundMessage })
							}
							label={__('No Entries Found', 'gp-entry-blocks')}
						/>
					</PanelBody>
				</>
			)}
		</InspectorControls>
	);

	useEffect(() => {
		// When the context changes, invalidate the store
		invalidateResolution('getEntryIds', [args]);
	}, [args.context]);

	const entryIds = useSelect(
		(select: any) => select('gp-entry-blocks').getEntryIds(args),
		[args]
	);

	const setDisplayLayout = (newDisplayLayout) =>
		setAttributes({
			layout: { ...layout, ...newDisplayLayout },
		});

	const displayLayoutControls = [
		{
			icon: list,
			title: __('List view'),
			onClick: () => setDisplayLayout({ type: 'default' }),
			isActive:
				!layoutType ||
				layoutType === 'default' ||
				layoutType === 'constrained',
		},
		{
			icon: grid,
			title: __('Grid view'),
			onClick: () =>
				setDisplayLayout({
					type: 'grid',
					columnCount,
				}),
			isActive: layoutType === 'grid',
		},
	];

	const blockContexts = entryIds.map((entryId) => ({ entryId }));

	let fadeOutPreviews = false;

	const isEditingContext = (entryId) => {
		return (
			isBlockOrChildSelected &&
			entryId === (activeBlockContextId ?? blockContexts[0].entryId)
		);
	};

	if (blockContexts.length) {
		for (const blockContext of blockContexts) {
			if (isEditingContext(blockContext.entryId)) {
				fadeOutPreviews = true;
			}
		}
	}

	return (
		<>
			<BlockControls>
				<ToolbarGroup controls={displayLayoutControls} />
			</BlockControls>
			{inspectorControls}
			<div {...blockProps}>
				{blockContexts &&
					blockContexts.map((blockContext) => (
						<BlockContextProvider
							key={blockContext.entryId}
							value={blockContext}
						>
							{isEditingContext(blockContext.entryId) ? (
								<div {...innerBlocksProps} />
							) : null}
							<MemoizedEntryTemplateBlockPreview
								blockArgs={{
									...args,
									context: {
										...args.context,
										'gp-entry-blocks/entryId':
											blockContext.entryId,
									},
								}}
								blockContextId={blockContext.entryId}
								setActiveBlockContextId={
									setActiveBlockContextId
								}
								fadeOut={fadeOutPreviews}
								isHidden={isEditingContext(
									blockContext.entryId
								)}
							/>
						</BlockContextProvider>
					))}
				{
					// Render InnerBlocks if there are no entries available to preview.
					!blockContexts.length && <div {...innerBlocksProps} />
				}
			</div>
		</>
	);
};

const Save = () => {
	const blockProps = useBlockProps.save();

	return (
		<div {...blockProps}>
			<InnerBlocks.Content />
		</div>
	);
};

export const settings = {
	edit: Edit,
	save: Save,
};
